import { getEntityType, getIDType } from '@datorama/akita';
import { AbstractUserRelatedDataService } from '@y42/feature';
import { AbstractFeatureAclService } from '@y42/feature/acl';
import { GitQuery } from '@y42/git';
import { ImpexService, TriggerService } from '@y42/impex';
import { AbstractJobService } from '@y42/job';
import { Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import {
  DatasourceGitService,
  DatasourceQuery,
  DatasourceState,
  DatasourceStore,
  DatasourceStoreModel,
  DatasourceTableJobHttpModel,
  DatasourceTableJobStore,
  DatasourceTableQuery,
  DatasourceTableStore,
} from '../state';

export abstract class DatasourceService<
  State extends DatasourceState<DatasourceStoreModel> = DatasourceState<DatasourceStoreModel>,
> extends ImpexService<State> {
  constructor(
    readonly cfg: {
      featureType: Y42FeatureType;
      userData: AbstractUserRelatedDataService<State>;
      git: DatasourceGitService;
      toast: ToastService;
      store: DatasourceStore<State>;
      query: DatasourceQuery<State>;
      acl: AbstractFeatureAclService<Y42FeatureType>;
      tableStore: DatasourceTableStore;
      tableQuery: DatasourceTableQuery;
      tableJobService: AbstractJobService<DatasourceTableJobHttpModel>;
      tableJobStore: DatasourceTableJobStore;
      gitQuery: GitQuery;
    },
    triggerService: TriggerService,
  ) {
    super(
      {
        featureType: cfg.featureType,
        store: cfg.store,
        query: cfg.query,
        userData: cfg.userData,
        acl: cfg.acl,
        git: cfg.git,
        gitQuery: cfg.gitQuery,
        toast: cfg.toast,
        childStores: [cfg.tableStore],
      },
      triggerService,
    );
  }

  protected readonly store = this.cfg.store;
  protected readonly query = this.cfg.query;
  protected readonly tableStore = this.cfg.tableStore;
  protected readonly tableJobStore = this.cfg.tableJobStore;
  protected readonly userData = this.cfg.userData;
  protected readonly git = this.cfg.git;
  protected readonly acl = this.cfg.acl;

  override async getAll(options?: { setLoading?: boolean | undefined } | undefined): Promise<void> {
    if (options?.setLoading) {
      this.store.setLoading(true);
    }

    await super.getAll({ setLoading: false });

    if (options?.setLoading) {
      this.store.setLoading(false);
    }
  }

  override async get($name: string, options?: { ignoreErrors?: boolean | undefined } | undefined) {
    const datasource = await super.get($name, options);
    return datasource;
  }

  async update(model: Partial<getEntityType<State>>, id = this.query.getActiveId() as getIDType<State>) {
    if (!id) {
      throw new Error(`[${this.cfg.featureType}] "${id}" not found.`);
    }

    this.store.update(id, model);
    await this.store.pending;
  }

  async delete($path = this.query.getActiveId() as getIDType<State>) {
    this.store.delete($path);
    await this.store.pending;
  }

  async deleteAuthorization() {
    const datasourceName = (this.query.getActive() as getEntityType<State>).$name;
    await this.git.deleteDatasourceAuthorization(datasourceName);
  }
}
